import Link from 'next/link'
import React, { useContext, useMemo } from 'react'

import { useRouter } from 'next/router'
import { Icon } from 'public/assets/v2/images/icons'
import { UserContext } from '../../../context'

import { ChatStoreContext } from '@/components/chat/ChatStore/ChatStoreContext'
import { ButtonComponent, CreatorlandLogo } from '@/components/commons'
import { cssClass } from '@/shared/utils'
import { colorsV2 } from '@/styles/v2/vars/vars.colors'
import { Badge } from '@mantine/core'
import { useChatConnectionsRequest } from 'containers/chat/hooks/useChatConnectionsRequest'
import { DealSyncSideBarCard } from 'containers/deal-sync/DealSyncSideBarCard'
import { DealSyncSidebarItem } from 'containers/deal-sync/DealSyncSidebarItem'
import { Notifications } from 'containers/notifications'
import { useNotifications } from 'containers/notifications/hooks/notifications'
import {
  useInvitationsReceived,
  useNewConnections,
} from 'containers/profile-network/hooks/useConnections'
import Image from 'next/image'
import { ProfilePageContext } from '../../../context/profilePage'
import { ReferralModal } from './ReferralModal'
import {
  sideBarBottomContent,
  sideBarTopContent,
  sidebarContainer,
  sidebarLogoContainer,
  sidebarMenuContainer,
  sidebarMenuList,
  sidebarMenuListItem,
  sidebarMenuListItemIcon,
  sidebarMenuListItemLabel,
  sidebarMenuListItemLink,
  sidebarMenuListItemUnreadMessages,
  sidebarMenuSublist,
  sidebarMenuSublistItem,
} from './Sidebar.css'

// TODO: joxxe - Refactor this component to create tabs navigation dinamically

export const Sidebar = () => {
  const [isNotificationsOpen, setIsNotificationsOpen] = React.useState(false)
  const { isAuthenticated, user } = React.useContext(UserContext)
  const userName = user?.username?.toLocaleLowerCase()
  const { invitationsCount } = useInvitationsReceived()
  const { newConnectionsCount } = useNewConnections()
  const { newNotifications } = useNotifications()

  const {
    hasUnreadMessages,
    connectionChannelsUnreadMessagesCount,
    dealChannelsUnreadMessagesCount,
  } = useContext(ChatStoreContext)

  const { ownProfile } = React.useContext(ProfilePageContext)
  // const isMod = useIsMod()
  // const { isSubscribed, subscriptions } = useSubscription()
  const { pathname } = useRouter()
  const [isReferralModalOpen, setIsReferralModalOpen] = React.useState(false)
  const requestConnections = useChatConnectionsRequest()

  const totalUnreadMessages = useMemo(
    () =>
      connectionChannelsUnreadMessagesCount +
      dealChannelsUnreadMessagesCount +
      requestConnections?.length,
    [
      connectionChannelsUnreadMessagesCount,
      dealChannelsUnreadMessagesCount,
      requestConnections?.length,
    ]
  )

  const unreadMessageCountLabel = useMemo(() => {
    if (!totalUnreadMessages) return ''

    if (totalUnreadMessages > 99) return '99+'

    return `${totalUnreadMessages}`
  }, [totalUnreadMessages])

  const isProfileSectionActive = useMemo(() => {
    if (!ownProfile && pathname !== '/insiderrewards') return false
    return (
      pathname === `/profile/[username]` ||
      pathname === `/[username]` ||
      pathname === `/[username]/projects` ||
      pathname === `/[username]/highlights` ||
      pathname === '/insiderrewards' ||
      pathname.includes('portfolio') ||
      pathname.includes('analytics')
    )
  }, [pathname, ownProfile])

  const isNetworkSectionActive = useMemo(() => {
    if (!ownProfile) return false
    return (
      pathname === `/[username]/network` ||
      pathname.includes('connections') ||
      pathname.includes('invitation') ||
      pathname.includes('cprofile')
    )
  }, [pathname, ownProfile])

  const isInboxSectionActive = useMemo(() => {
    return pathname.includes('inbox')
  }, [pathname])

  const handleHelpClick = () => {
    if (window.productFruitsIsReady) {
      window.productFruits?.api?.feedback?.showModal()
    }
  }

  const networkNotificationsCount = newConnectionsCount + invitationsCount

  return (
    <>
      <ReferralModal
        isOpen={isReferralModalOpen}
        onClose={() => setIsReferralModalOpen(false)}
      />
      <div className={sidebarContainer} data-visible={isAuthenticated}>
        <div className={sidebarLogoContainer}>
          <Link href="/">
            <CreatorlandLogo />
          </Link>
        </div>
        <div className={sideBarTopContent}>
          <div className={sidebarMenuContainer}>
            <DealSyncSideBarCard />
            <ul className={sidebarMenuList}>
              <li className={sidebarMenuListItem}>
                <Link href="/discover">
                  <div
                    className={sidebarMenuListItemLink}
                    data-active={pathname.includes('discover')}
                  >
                    <span className={sidebarMenuListItemIcon}>
                      <Icon.DiscoverIcon />
                    </span>
                    <span className={sidebarMenuListItemLabel}>Discover</span>
                  </div>
                </Link>
              </li>

              <li className={sidebarMenuListItem}>
                <Link href={`/${userName}`}>
                  <div
                    className={sidebarMenuListItemLink}
                    data-active={isProfileSectionActive}
                  >
                    <span className={sidebarMenuListItemIcon}>
                      <Icon.ProfileIcon />
                    </span>
                    <span className={sidebarMenuListItemLabel}>Profile</span>
                  </div>
                </Link>

                <ul
                  className={sidebarMenuSublist}
                  data-active={isProfileSectionActive}
                >
                  <Link href={`/${userName}`}>
                    <li
                      data-active={pathname === '/[username]'}
                      className={sidebarMenuSublistItem}
                    >
                      Overview
                    </li>
                  </Link>

                  <Link href={`/${userName}/analytics`}>
                    <li
                      data-active={pathname.includes('analytics')}
                      className={sidebarMenuSublistItem}
                    >
                      Analytics
                    </li>
                  </Link>

                  <Link href={`/${userName}/projects`}>
                    <li
                      data-active={pathname.includes('projects')}
                      className={sidebarMenuSublistItem}
                    >
                      Projects
                    </li>
                  </Link>

                  <Link href={`/${userName}/highlights`}>
                    <li
                      data-active={pathname.includes('highlights')}
                      className={sidebarMenuSublistItem}
                    >
                      Highlights
                    </li>
                  </Link>

                  <Link
                    href="https://newsletter.creatorland.com/c/university"
                    target="_blank"
                  >
                    <li
                      data-active={pathname.includes('insiderrewards')}
                      className={cssClass([
                        sidebarMenuSublistItem,
                        'flex items-center',
                      ])}
                    >
                      University
                    </li>
                  </Link>
                </ul>
              </li>

              <DealSyncSidebarItem />

              <li
                className={sidebarMenuListItem}
                onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
              >
                <div className={sidebarMenuListItemLink}>
                  <span className={sidebarMenuListItemIcon}>
                    <Icon.BellIcon />
                  </span>
                  <span className={sidebarMenuListItemLabel}>
                    Notifications
                  </span>
                  {newNotifications > 0 && (
                    <div
                      className={cssClass([
                        sidebarMenuListItemUnreadMessages,
                        isNotificationsOpen
                          ? 'bg-green text-white'
                          : 'bg-danger-light',
                      ])}
                    >
                      {newNotifications}
                    </div>
                  )}
                  <Notifications
                    isOpen={isNotificationsOpen}
                    onClose={() => setIsNotificationsOpen(false)}
                  />
                </div>
              </li>

              <li className={sidebarMenuListItem}>
                <Link href={`/${userName}/network`}>
                  <div
                    className={sidebarMenuListItemLink}
                    data-active={isNetworkSectionActive}
                  >
                    <span className={sidebarMenuListItemIcon}>
                      <Icon.ProfilesIcon />
                    </span>
                    <span className={sidebarMenuListItemLabel}>My Network</span>
                    {networkNotificationsCount > 0 ? (
                      <div
                        className={cssClass([
                          sidebarMenuListItemUnreadMessages,
                          isNetworkSectionActive
                            ? 'bg-green text-white'
                            : 'bg-danger-light',
                        ])}
                      >
                        {networkNotificationsCount > 99
                          ? '99+'
                          : networkNotificationsCount}
                      </div>
                    ) : null}
                  </div>
                </Link>

                <ul
                  className={sidebarMenuSublist}
                  data-active={isNetworkSectionActive}
                >
                  <Link href={`/${userName}/network/connections`}>
                    <li
                      data-active={pathname.includes('connections')}
                      className={sidebarMenuSublistItem}
                    >
                      Connections
                    </li>
                  </Link>
                </ul>
              </li>

              <li className={sidebarMenuListItem}>
                <Link href="/inbox">
                  <div
                    className={sidebarMenuListItemLink}
                    data-active={isInboxSectionActive}
                  >
                    <span className={sidebarMenuListItemIcon}>
                      <Icon.InboxIcon />
                    </span>
                    <span className={sidebarMenuListItemLabel}>Inbox</span>
                    {hasUnreadMessages || !!totalUnreadMessages ? (
                      <div
                        className={cssClass([
                          sidebarMenuListItemUnreadMessages,
                          isInboxSectionActive
                            ? 'bg-green text-white'
                            : 'bg-danger-light',
                        ])}
                      >
                        {unreadMessageCountLabel}
                      </div>
                    ) : null}
                  </div>
                </Link>

                {/* {(isMod ||
                  // subs + freebird users
                  isSubscribed ||
                  subscriptions?.[SubscriptionProductNames.FREEBIRD] ===
                    true) && (
                  <ul
                    className={sidebarMenuSublist}
                    data-active={isInboxSectionActive}
                  >
                    <Link href="/inbox/cocreator">
                      <li
                        data-active={pathname === '/inbox/cocreator'}
                        className={sidebarMenuSublistItem}
                      >
                        <div className="flex items-center gap-8">
                          Co-Creator
                          <Badge
                            size="xs"
                            color={colorsV2.accent.light.tertiary}
                            c="white"
                          >
                            BETA
                          </Badge>
                        </div>
                      </li>
                    </Link>
                  </ul>
                )} */}
              </li>

              <Link href="/jobs">
                <li className={sidebarMenuListItem}>
                  <div
                    className={sidebarMenuListItemLink}
                    data-active={pathname === '/jobs'}
                  >
                    <span className={sidebarMenuListItemIcon}>
                      <Icon.JobsIcon />
                    </span>
                    <span className={sidebarMenuListItemLabel}>Jobs</span>
                  </div>
                </li>
              </Link>

              <li className={sidebarMenuListItem}>
                <Link href="/search">
                  <div
                    className={sidebarMenuListItemLink}
                    data-active={pathname === '/search'}
                  >
                    <span className={sidebarMenuListItemIcon}>
                      <Icon.SearchIcon />
                    </span>
                    <span className={sidebarMenuListItemLabel}>Search</span>
                    {pathname !== '/search' && (
                      <Badge
                        size="sm"
                        color={colorsV2.accent.light.primary}
                        c="black"
                        className="ml-auto"
                      >
                        NEW
                      </Badge>
                    )}
                  </div>
                </Link>
              </li>

              <Link href="/settings">
                <li className={sidebarMenuListItem}>
                  <div
                    className={sidebarMenuListItemLink}
                    data-active={pathname === '/settings'}
                  >
                    <span className={sidebarMenuListItemIcon}>
                      <Icon.SettingsIcon />
                    </span>
                    <span className={sidebarMenuListItemLabel}>Settings</span>
                  </div>
                </li>
              </Link>

              <Link
                href="https://chatgpt.com/g/g-67361d0e1a888190911b1688ec990a2d-creatorland-co-creator"
                target="_blank"
              >
                <li className={sidebarMenuListItem}>
                  <div className={sidebarMenuListItemLink}>
                    <Image
                      alt=""
                      width={16}
                      height={16}
                      src={Icon.CoCreatorPNG}
                    />
                    <span className={sidebarMenuListItemLabel}>Co-Creator</span>
                  </div>
                </li>
              </Link>

              <Link href="/checkout">
                <li className={sidebarMenuListItem}>
                  <div
                    className={sidebarMenuListItemLink}
                    data-active={pathname === '/checkout'}
                  >
                    <span className={sidebarMenuListItemIcon}>
                      <Image
                        alt=""
                        width={16}
                        height={16}
                        src={Icon.InsidersLifeTimeBadgePNG}
                      />
                    </span>
                    <span className={sidebarMenuListItemLabel}>
                      Get Premium
                    </span>
                  </div>
                </li>
              </Link>
            </ul>
          </div>
        </div>
        <div className={sideBarBottomContent}>
          <ButtonComponent
            variation="secondary"
            fullWidth
            label="Feedback"
            onClick={() => handleHelpClick()}
            size="xSmall"
          />
          <ButtonComponent
            variation="primary"
            fullWidth
            label="Refer a friend"
            onClick={() => setIsReferralModalOpen(true)}
            size="xSmall"
          />
        </div>
      </div>
    </>
  )
}
